/**
 * `/projects/:id/live` — admin Live overview, admin edit (PRD §4.4, §7.2).
 *
 * G-Admin sees three steps as Übersicht / Mitarbeiter / Kommunikation.
 * Company admin has no Live tab strip — overview only; employees stay on /employees.
 * The default landing tab is always Übersicht (step 3), even when the project has no
 * assignees yet. Tab + year live in the URL (`?tab=` / `?year=`) so refresh keeps the view.
 * Month-lock UI is temporarily hidden on Übersicht (locks remain on
 * Admin → Festschreibung); the reference also keeps no lock UI on this page.
 */

import { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BackLink } from "@/components/back-link";
import { TrackingCalendar } from "@/components/tracking/calendar";
import { LiveStepNav, type LiveStep } from "@/components/tracking/live-step-nav";
import { ProjectEmployeesStep } from "@/components/tracking/project-employees-step";
import { ProjectCommunicationStep } from "@/components/tracking/project-communication-step";
import { ProjectOverviewStep } from "@/components/tracking/project-overview-step";
import { ProjectWeekNotesSection } from "@/components/tracking/project-week-notes-section";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { exportProjectYearMatrix } from "@/lib/export-xlsx";
import { resolveLiveBack } from "@/lib/live-back";
import { useQueryYear } from "@/lib/query-param";
import { useQueryTab } from "@/lib/query-tab";
import { useSession } from "@/lib/session";
import { useProject } from "@/lib/tenancy";
import {
  useCalendar,
  useDownloadAttachmentsZip,
  useDownloadStundenzettel,
  useLiveOverview,
  useLogExport,
} from "@/lib/tracking";
import { berlinToday } from "@/shared/dates";
import { projectYears } from "@/shared/years";
import type { LiveEmployeeMonth } from "@/shared/schemas/tracking";

const LIVE_TABS = ["overview", "employees", "communication"] as const;
type LiveTab = (typeof LIVE_TABS)[number];

const TAB_TO_STEP: Record<LiveTab, LiveStep> = {
  overview: 3,
  employees: 2,
  communication: 4,
};

const STEP_TO_TAB: Record<LiveStep, LiveTab> = {
  3: "overview",
  2: "employees",
  4: "communication",
};

/** Project term as years for the PDP subtitle (not the Übersicht filter year). */
function projectTimeframeLabel(
  startDate: string,
  endDate: string,
): {
  key: "projectLive.timeframeSingle" | "projectLive.timeframe";
  values: Record<string, number>;
} {
  const startYear = Number(startDate.slice(0, 4));
  const endYear = Number(endDate.slice(0, 4));
  if (startYear === endYear) {
    return { key: "projectLive.timeframeSingle", values: { year: startYear } };
  }
  return { key: "projectLive.timeframe", values: { start: startYear, end: endYear } };
}

export function ProjectLivePage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useSession();
  const isGAdmin = user?.role === "grantonomy_admin";
  const projectQuery = useProject(id);
  const today = berlinToday();
  const todayYear = Number(today.slice(0, 4));
  const [editing, setEditing] = useState<LiveEmployeeMonth | null>(null);
  const [tab, setTab] = useQueryTab("tab", LIVE_TABS, "overview");

  const project = projectQuery.data?.project;
  const years = useMemo(
    () => (project ? projectYears(project.startDate, project.endDate, todayYear) : []),
    [project, todayYear],
  );
  const [year, setYear] = useQueryYear("year", years, todayYear);

  const overview = useLiveOverview(id, year);
  const logExport = useLogExport();
  const downloadAttachmentsZip = useDownloadAttachmentsZip(id ?? "", year);
  const downloadStundenzettelZip = useDownloadStundenzettel(id ?? "");

  const locale = i18n.language === "en" ? "en-US" : "de-DE";
  const monthsShort = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Date(2024, i, 1).toLocaleDateString(locale, { month: "short" }),
      ),
    [locale],
  );

  if (projectQuery.isLoading || overview.isLoading) {
    return <Skeleton className="h-40 w-full rounded-xl" />;
  }

  if (!project || !overview.data) {
    return <p className="text-sm text-muted-foreground">{t("errors.NOT_FOUND")}</p>;
  }

  // Visual order: Übersicht first; default selection is always Übersicht.
  // CA: overview only (no tab chrome); G-Admin: Übersicht / Mitarbeiter / Kommunikation.
  const steps: LiveStep[] = isGAdmin ? [3, 2, 4] : [3];
  const requestedStep = TAB_TO_STEP[tab];
  const activeStep: LiveStep = steps.includes(requestedStep) ? requestedStep : 3;
  const timeframe = projectTimeframeLabel(project.startDate, project.endDate);
  const defaultBackTo = isGAdmin ? `/companies/${project.companyId}?tab=projects` : "/projects";
  const fromState =
    location.state && typeof location.state === "object" && "from" in location.state
      ? (location.state as { from?: unknown }).from
      : undefined;
  const back = resolveLiveBack(fromState, defaultBackTo);

  const handleExportMatrix = () => {
    if (!user) {
      toast.error(t("projectLive.exportFailed"));
      return;
    }
    try {
      exportProjectYearMatrix(overview.data, {
        grantonomy_admin: t("appShell.role.grantonomy_admin"),
        company_admin: t("appShell.role.company_admin"),
        employee: t("appShell.role.employee"),
      });
      void logExport.mutateAsync({
        type: "export_year_matrix",
        projectId: project.id,
        year,
        format: "xlsx",
      });
      toast.success(t("projectLive.exportDone"));
    } catch {
      toast.error(t("projectLive.exportFailed"));
    }
  };

  const handleExportAttachmentsZip = () => {
    if (!id) return;
    void downloadAttachmentsZip
      .mutateAsync()
      .then(() => toast.success(t("tracker.attachmentsZipDone")))
      .catch(() => toast.error(t("tracker.attachmentsZipFailed")));
  };

  const handleExportStundenzettelZip = () => {
    if (!id) return;
    void downloadStundenzettelZip
      .mutateAsync({ year })
      .then(() => toast.success(t("projectLive.exportStundenzettelZipDone")))
      .catch(() => toast.error(t("projectLive.exportStundenzettelZipFailed")));
  };

  return (
    <div>
      <BackLink to={back.to} data-testid="project-live-back">
        {t(back.labelKey)}
      </BackLink>

      <div className="mt-4" data-testid="project-live-header">
        <div
          className="text-xs font-medium uppercase tracking-widest text-muted-foreground"
          data-testid="project-live-company-timeframe"
        >
          {overview.data.companyName} · {t(timeframe.key, timeframe.values)}
        </div>
        <h1 className="mt-2 text-5xl font-display">{project.name}</h1>
      </div>

      {steps.length > 1 && (
        <LiveStepNav
          steps={steps}
          active={activeStep}
          onSelect={(next) => setTab(STEP_TO_TAB[next])}
        />
      )}

      <div
        role={steps.length > 1 ? "tabpanel" : undefined}
        id={`live-panel-${activeStep}`}
        aria-labelledby={steps.length > 1 ? `live-tab-${activeStep}` : undefined}
      >
        {activeStep === 2 && <ProjectEmployeesStep project={project} canManage={isGAdmin} />}

        {activeStep === 4 && isGAdmin && id && <ProjectCommunicationStep projectId={id} />}

        {activeStep === 3 && (
          <>
            <ProjectOverviewStep
              overview={overview.data}
              year={year}
              years={years}
              onYearChange={setYear}
              monthsShort={monthsShort}
              onExportMatrix={handleExportMatrix}
              onExportAttachmentsZip={handleExportAttachmentsZip}
              onExportStundenzettelZip={handleExportStundenzettelZip}
              onEdit={setEditing}
            />

            {id && <ProjectWeekNotesSection projectId={id} year={year} />}
          </>
        )}
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("adminEdit.title", {
                name: editing ? `${editing.firstName} ${editing.lastName}` : "",
              })}
            </DialogTitle>
            <DialogDescription>{t("adminEdit.description", { year })}</DialogDescription>
          </DialogHeader>
          {editing && id && (
            <AdminCalendar
              userId={editing.userId}
              userName={editing.firstName}
              personLastName={editing.lastName}
              projectId={id}
              year={year}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminCalendar({
  userId,
  userName,
  personLastName,
  projectId,
  year,
}: {
  userId: string;
  userName: string;
  personLastName: string;
  projectId: string;
  year: number;
}) {
  const today = berlinToday();
  const berlinYear = Number(today.slice(0, 4));
  const month = year === berlinYear ? Number(today.slice(5, 7)) : 1;
  const calendar = useCalendar({
    userId,
    projectId,
    year,
    month,
    asAdmin: true,
  });

  // Never paint another person's cached calendar under this userId (query-key races).
  if (calendar.isLoading || !calendar.data || calendar.data.userId !== userId) {
    return <Skeleton className="h-40 w-full rounded-xl" />;
  }

  return (
    <TrackingCalendar
      key={`${userId}-${calendar.data.year}-${calendar.data.projectId}`}
      calendar={calendar.data}
      userId={userId}
      userName={userName}
      personLastName={personLastName}
      asAdmin
    />
  );
}
